/* Core & libraries */
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { useGenericApiFetch }  from "../endpoints/base";
import {
  PAYMENT_GRID_ENDPOINT,
  DELETE_PAYMENT_ENDPOINT,
  GET_CARTID_ENDPOINT,
  ADD_PAYMENT_ENDPOINT,
  EDIT_PAYMENT_ENDPOINT,
  GET_ASSIGN_PAYMENT_GRID_ENDPOINT,
  ASSIGN_CARD_ENDPOINT,
  GET_TEAM_PAYMENTS_ENDPOINT,
  UPDATE_TEAM_SETTINGS_TOGGLE_ENDPOINT,
  CHANGE_PAYMENT_METHOD_ENDPOINT,
  CHECK_CARDS_EXPIRING,
  GET_DATA_SHARING,
  PAYMENT_TOKEN, UPDATE_TEAM_PAYMENT_METHOD_ENDPOINT,
} from "../endpoints/endpoints";
import { useNotificationContext } from "../contexts/notificationContext";
import { useUserContext } from "../contexts/userContext";
import { useGlobalUpdateContext } from "contexts/globalContext";
import { exipiryCardsDuration } from "constants/appConstants";

/* Components */
/* Styles */

export const usePaymentService = (props = {}) => {
  const [paymentResponse, setPaymentResponse] = useState({})
  const [paymentMethods, setPaymentMethods] = useState();
  const { genericApiFetch } = useGenericApiFetch();
  const { setNotification } = useNotificationContext();
  const userDetails = useUserContext();
  const setGlobalContext  = useGlobalUpdateContext();
  const [paymentToken, setPaymentToken] = useState(null);
  const [maxCardsAssignment, setMaxCardsAssignment] = useState(1);

  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  useEffect(() => {
    (async () => {
      await getPaymentList();
    })();
  }, []);

  const getPaymentList = async (options, setPaymentResponseData) => {
    if (options) {
      const response = await genericApiFetch({
        url: PAYMENT_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId),
        method: "GET",
        queryParams: options,
        spinner: "paymentsGridSpinner"
      });
      if (response.success) {
        setPaymentResponse(response.data);
        if(setPaymentResponseData) {
          setPaymentResponseData(response.data);
        }
      } else {
        setPaymentResponse({ results: null });
      }
    }
  };

  const getCardsExpiringData = async (companyEntityId) => {
    const reqBody = {
      numberOfDaysTillExpiry: exipiryCardsDuration[providerCode]
    }

    const response = await genericApiFetch({
      url: CHECK_CARDS_EXPIRING.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data : reqBody,
    });
    if (response.success) {
      setGlobalContext((prevGlobalState) => ({
        ...prevGlobalState,
        showExpiryCreditCards: response.data.numberOfCardsExpiring > 0,
      }))
      return response.data
    }
    return false;
  }

  const assignPaymentCard = async (groupData) => {
    const response = await genericApiFetch({
      url: ASSIGN_CARD_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data : groupData.requestBody,
      spinner: "confirmAssignSpinner"
    })
    if (response.success) {
      if (response?.data?.messageData?.warningMessages?.find(item => parseInt(item.code) === 9000)) {
        setNotification(
          "error",
          <FormattedMessage id="notifications.assignPayment.warningTitle" />,
          <FormattedMessage id="notifications.assignPayment.warningText" />
        )
      } else {
        setNotification(
          "success",
          <FormattedMessage id="notifications.assignPayment.successTextTitle" />,
          <FormattedMessage id="notifications.assignPayment.successTextDescription" values={{vendorName: groupData.vendorName, cardNumberMasked: groupData.cardInfo.cardNumberMasked}}/>
        )
      }

      return true;
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.assignPayment.errorTitle" />,
      <FormattedMessage id="notifications.assignPayment.errorText" />
    )
    return false
  };

  const getAssignTeamsData = async (spinner="assignTeamsButton", fopUUID) => {
    const response = await genericApiFetch({
      url: GET_ASSIGN_PAYMENT_GRID_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{fopUUID}", fopUUID),
      method: "GET",
      spinner: spinner
    })

    if (response.success) {
      return response.data;
    }
    return false
  };

  const deletePayment = async (entity) => {
    const response = await genericApiFetch({
      url: DELETE_PAYMENT_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId).replace("{fopUUID}", entity.fopUUID),
      method: "DELETE",
      spinner: "deleteActionSpinner",
    });
    if (response.success) {
      await getCardsExpiringData(companyEntityId);
      return true;
    }
    return false;
  }
  
  const getPaymentMethods = async (teamId) => {
    const response = await genericApiFetch({
      url: GET_TEAM_PAYMENTS_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{groupEntityId}", teamId),
      method: "GET",
    });
    if (response.success) {
      setPaymentMethods(response?.data?.fops)
      setMaxCardsAssignment(response?.data?.maxNumberCards)
    }
  };

  const changePaymentMethod = async (payload) => {
    const response = await genericApiFetch({
      url: CHANGE_PAYMENT_METHOD_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data : payload,
      spinner: "changePaymentSpinner",
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.changePaymentSuccessTitle" />,
        <FormattedMessage id="notification.changePaymentSuccessText"/>
      )
      return true
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.changePaymentErrorTitle" />,
      <FormattedMessage id="notifications.changePaymentErrorText" />
    )

    return false
  }

  const updateTeamPaymentMethod = async (payload) => {
    const response = await genericApiFetch({
      url: UPDATE_TEAM_PAYMENT_METHOD_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "PUT",
      data : payload,
      spinner: "changePaymentSpinner",
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.changePaymentSuccessTitle" />,
        payload?.fopUUIDRefIds.length ? 
        <FormattedMessage id="notification.changePaymentSuccessText"/> :
        <FormattedMessage id="notification.changePaymentToNoneSuccessText"/>
      )
      return true
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.changePaymentErrorTitle" />,
      <FormattedMessage id="notifications.changePaymentErrorText" />
    )

    return false
  }

  const updateTeamSettingsToggle = async (reqBody) => {
    const response = await genericApiFetch({
      url: UPDATE_TEAM_SETTINGS_TOGGLE_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: reqBody
    })
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.togglePaymentSuccessTitle" />,
        <FormattedMessage id="notification.togglePaymentSuccessText"/>
      )
      return true
    }
    return false
  };
  
  const getCartId = async (spinner, createCard, fopUUID) => {
    const response = await genericApiFetch({
      url: GET_CARTID_ENDPOINT.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "POST",
      spinner: spinner,
      data: {
        "createCard": createCard,
        "externalReferenceId": fopUUID
      },
    });
    if (response.success) {
      return response?.data;
    }
    return false;
  }

  const addPaymentCard = async (payload) => {
    const response = await genericApiFetch({
      url: ADD_PAYMENT_ENDPOINT.replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: payload
    });
    if (response.success) {
      setNotification(
        "success",
        <FormattedMessage id="notification.addPaymentSuccessTitle" />,
        <FormattedMessage
          id="notification.addPaymentSuccessText"
          values={{
            cardType: payload?.cardType,
            cardNumber: payload?.lastFourDigits
          }}
        />
      )
      return response?.data;
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.revokeErrorTitle" />,
      <FormattedMessage id="notifications.revokeErrorText" />
    )

    return false;
  }

  const editPaymentCard = async (payload, fopUUID) => {
    const response = await genericApiFetch({
      url: EDIT_PAYMENT_ENDPOINT.replace("{companyEntityId}", companyEntityId).replace("{fopUUID}", fopUUID),
      method: "PATCH",
      data: payload
    });
    if (response.success) {
      await getCardsExpiringData(companyEntityId);
      return response?.data;
    }

    setNotification(
      "error",
      <FormattedMessage id="notifications.revokeErrorTitle" />,
      <FormattedMessage id="notifications.revokeErrorText" />
    )

    return false;
  }

  const getPaymentToken = async ({spinner, payload}) => {
    const response = await genericApiFetch({
      url: PAYMENT_TOKEN.replace("{providerCode}", providerCode?.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      method: "POST",
      data: payload,
      spinner: spinner
    });

    if (response?.success) {
      setPaymentToken(response?.data?.token)

      return true;
    }
    return false;
  };

  return {
    paymentResponse,
    setPaymentResponse,
    getPaymentList,
    getAssignTeamsData,
    deletePayment,
    paymentMethods,
    getPaymentMethods,
    changePaymentMethod,
    assignPaymentCard,
    updateTeamSettingsToggle,
    getCartId,
    addPaymentCard,
    editPaymentCard,
    getCardsExpiringData,
    paymentToken,
    getPaymentToken,
    updateTeamPaymentMethod,
    maxCardsAssignment,
  };
}

